<template>
  <div id="linkedin_account">
    <!-- Loading State -->
    <div v-if="isLoading">
      <div class="flex flex-row justify-between items-center w-full mb-6">
        <a-skeleton-input :active="true" size="small" style="width: 200px" />
        <a-skeleton-button :active="true" size="default" />
      </div>
      <a-skeleton :active="true" :paragraph="{ rows: 8 }" />
    </div>
    <!-- Main Content -->
    <div v-else-if="isSuccess && !showBillingModal">
      <div class="flex flex-row justify-between items-center w-full">
        <div class="flex-grow">
          <h1 class="text-xl text_color font-semibold">Personal LinkedIn</h1>
        </div>
        <div class="flex items-center gap-4">
          <a-button 
            class="primary_medium" 
            @click="connectLinkedInAccount"
            :loading="isConnecting"
          >
            Connect LinkedIn
          </a-button>
        </div>
      </div>
      <!-- Disconnect Confirmation Modal -->
      <CustomModal
        v-model:visible="isDisconnectModalVisible"
        title="Disconnect LinkedIn Account"
        width="32vw"
        :close-on-outside-click="false"
      >
        <span class="text_color">
          <span v-if="accountToDisconnect">
            Disconnecting LinkedIn account <strong>{{ accountToDisconnect.name }} ({{ accountToDisconnect.email }})</strong> 
            will cause all the scheduled steps from this account to be skipped, impacting active campaigns and {{ featureNames.SEQUENCES }}.
            Are you sure you want to disconnect?.
          </span>
          <span v-else>
            Disconnecting your LinkedIn account will cause all scheduled LinkedIn
            actions to be skipped, impacting active campaigns and {{ featureNames.SEQUENCES }}.
          </span>
          Are you sure you want to disconnect?
        </span>
        <template #footer>
          <a-button @click="hideDisconnectModal" class="secondary_medium">
            Cancel
          </a-button>
          <a-button @click="handleDisconnect" class="primary_medium ml-2">
            Disconnect
          </a-button>
        </template>
      </CustomModal>

      <!-- Delete Confirmation Modal -->
      <CustomModal
        v-model:visible="isDeleteModalVisible"
        title="Delete LinkedIn Account"
        width="32vw"
        :close-on-outside-click="false"
      >
        <span class="text_color">
          <span v-if="accountToDelete">
            Are you sure you want to delete LinkedIn account 
            <strong v-if="accountToDelete.name && accountToDelete.email">
              {{ accountToDelete.name }} ({{ accountToDelete.email }})
            </strong>
            <strong v-else-if="accountToDelete.name">
              {{ accountToDelete.name }}
            </strong>
            <strong v-else-if="accountToDelete.email">
              {{ accountToDelete.email }}
            </strong>
            <strong v-else>
              this account
            </strong>?
          </span>
        </span>
        <template #footer>
          <a-button @click="hideDeleteModal" class="secondary_medium">
            Cancel
          </a-button>
          <a-button @click="handleDelete" class="primary_medium ml-2">
            Delete
          </a-button>
        </template>
      </CustomModal>

      <!-- LinkedIn Accounts Table -->
      <div class="mt-6">
        <CustomTable
          v-if="linkedinAccounts.length > 0"
          :headers="tableHeaders"
          :data="linkedinAccounts"
          :custom-row="customRow"
          :show-actions-column="true"
          :custom-column-render="customColumnRender"
          :actions="tableActions"
          :add-side-border="true"
          height="calc(100vh - 300px)"
        />

        <div v-else class="flex items-center justify-center min-h-screen w-full -mt-20">
          <div class="flex flex-col items-center gap-2">
            <img
                src="https://super-sales-public-files20240730144930944300000001.s3.us-east-1.amazonaws.com/super_sales_integrations_icons/linkedin_icon.svg"
                alt="empty_screen"
            />
            <span>Connect Your LinkedIn Account</span>
            <span class="text_color text-base proxima_nova opacity-60 font-normal"
            >Connect Linkedin to send invites and messages directly through
            SuperAGI
          </span>
            <a-button class="primary_medium" @click="connectLinkedInAccount">Connect</a-button>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="!isLoading && showBillingModal">
      <div class="flex items-center justify-center min-h-screen w-full -mt-20">
        <div class="flex flex-col items-center gap-2">
          <span>Add Credits to Connect Your LinkedIn Account</span>
          <span class="text_color text-base proxima_nova opacity-60 font-normal"
            >Say goodbye to seat-based pricing and do more work automatically
            with AI</span
          >
          <a-button class="primary_medium" @click="moveToBilling(true)"
            >Buy AI Credits Now</a-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed } from "vue";
import DashboardService from "@/src/api/DashboardService.js";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { moveToBilling } from "@/src/utils/utils.js";
import BillingService from "@/src/api/BillingService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import { featureNames } from "@/src/constants/globalConstants";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import { useRouter } from "vue-router";
import LinkedinRow from "@/src/components/LinkedinRow.vue";
import mqttService from "@/src/services/mqtt/mqttService.js";
import { getCurrentEnvironment } from "@/src/constants/environmentConstants.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";

const isDisconnectModalVisible = ref(false);
const accountToDisconnect = ref(null);
const isDeleteModalVisible = ref(false);
const accountToDelete = ref(null);
let isConnected = ref(false);
let account = ref(null);
let isSuccess = ref(false);
const showBillingModal = ref(false);
const isLoading = ref(true);

// Billing related refs
const billingVersion = ref(null);
const hasPaidPlan = ref(false);
const billingStatus = ref(null);
const recentBillingAccount = ref(false);

const bypassBillingRestrictions = computed(() => {
  return !!localStorage.getItem("bypass_billing_restrictions");
});

const fetchBillingStatus = async () => {
  try {
    const response = await BillingService.getBillingAccount();
    if (response?.success) {
      if (response?.account) {
        billingVersion.value = response.billing_version;
        hasPaidPlan.value = response.account.has_paid_plan;
        billingStatus.value = response.account.status;
        recentBillingAccount.value = response.recent_billing_account;

        if (billingStatus.value == "FREE_PLAN" && !bypassBillingRestrictions.value) {
          showBillingModal.value = true;
        }
      } else {
        hasPaidPlan.value = false;
      }
    }
  } catch (error) {
    console.error("Error fetching paid status:", error);
    billingStatus.value = null;
    hasPaidPlan.value = false;
  }
};

const customColumnRender = {
  account_name: LinkedinRow,
};

const checkConnection = async () => {
  const data = { is_primary: true };
  account = await DashboardService.getPrimaryLinkedInAccount(data);
  if (account && account.success) {
    isConnected.value = true;
  }
  isSuccess.value = true;
};

const connectLinkedInAccount = async () => {
  try {
    isConnecting.value = true;
    const data = { is_primary: true };

    const response = await DashboardService.connectPrimaryLinkedInAccount(data);
    if (response.success && response.redirect_url) {
      window.open(response.redirect_url);
    } else if (response.error) {
      message.error(response.error);
    }
  } catch (error) {
    console.error("Error connecting linkedin:", error);
    message.error("Failed to connect LinkedIn account");
  } finally {
    isConnecting.value = false;
  }
};

// Hide modals
const hideDisconnectModal = () => {
  isDisconnectModalVisible.value = false;
  accountToDisconnect.value = null;
};

const hideDeleteModal = () => {
  isDeleteModalVisible.value = false;
  accountToDelete.value = null;
};

const handleDisconnect = async () => {
  try {
    // If accountToDisconnect is set, disconnect specific account
    if (accountToDisconnect.value) {
      const data = { 
        is_primary: accountToDisconnect.value.is_primary,
        integration_id: accountToDisconnect.value.id 
      };
      const response = await DashboardService.disconnectLinkedIn(data);
      if (response && response.success) {
        message.success("LinkedIn account disconnected successfully");
        await fetchLinkedInAccounts();
        hideDisconnectModal();
      }
    }
  } catch (error) {
    hideDisconnectModal();
    message.error("Failed to disconnect LinkedIn account");
  }
};

const router = useRouter();
const linkedinAccounts = ref([]);
const isConnecting = ref(false);

const tableHeaders = ref([
  {
    key: "account_name",
    label: "LinkedIn Account",
    width: 5,
  },
  {
    key: "status",
    label: "Status",
    width: 2,
  },
  {
    key: "max_connections",
    label: "Connections/day",
    width: 2,
  },
  {
    key: "max_messages",
    label: "Messages/day",
    width: 2,
  },
  {
    key: "max_inmails",
    label: "InMails/day",
    width: 2,
  },
]);

const tableActions = [
  {
    label: "Edit",
    handler: (row) => editAccount(row),
    icon: imageNames.EDIT,
  },
  {
    label: "Disconnect",
    handler: (row) => disconnectAccount(row),
    icon: imageNames.DISCONNECT_GREY,
    condition: (row) => row.status === 'Connected',
  },
  {
    label: "Reconnect",
    handler: (row) => reconnectAccount(row),
    icon: imageNames.LINK_GREY,
    condition: (row) => row.status === 'Disconnected',
  },
  {
    label: "Mark as primary",
    handler: (row) => markPrimary(row),
    icon: imageNames.STAR_GREY,
    condition: (row) => row.is_default === false && row.status === 'Connected',
  },
  {
    label: "Delete",
    handler: (row) => deleteAccount(row),
    icon: imageNames.DELETE_GREY,
    condition: (row) => row.status === 'Disconnected',
  },
];

const markPrimary = async (row) => {
  try {
    const response = await DashboardService.setLinkedInAsPrimary(row.id);
    if (response && response.success) {
      message.success("LinkedIn account set as primary successfully");
      await fetchLinkedInAccounts(); // Refresh the accounts list
    } else {
      message.error(response.error || "Failed to set LinkedIn account as primary");
    }
  } catch (error) {
    console.error("Error setting LinkedIn account as primary:", error);
    message.error("Failed to set LinkedIn account as primary");
  }
};

const editAccount = (row) => {
  router.push(`/settings/linkedin_account/${row.id}`);
};

const disconnectAccount = (row) => {
  accountToDisconnect.value = row;
  isDisconnectModalVisible.value = true;
};

const reconnectAccount = async (row) => {
  try {
    const data = { is_primary: row.is_primary, integration_id: row.id };
    const response = await DashboardService.connectPrimaryLinkedInAccount(data);
    if (response && response.success && response.redirect_url) {
      window.open(response.redirect_url);
    } else if (response.error) {
      message.error(response.error);
    }
  } catch (error) {
    console.error("Error reconnecting linkedin:", error);
    message.error("Failed to reconnect LinkedIn account");
  }
};

const deleteAccount = (row) => {
  accountToDelete.value = row;
  isDeleteModalVisible.value = true;
};

const handleDelete = async () => {
  try {
    if (accountToDelete.value) {
      const response = await DashboardService.deleteLinkedInAccount(accountToDelete.value.id);
      if (response.success) {
        message.success("LinkedIn account deleted successfully");
        await fetchLinkedInAccounts();
        hideDeleteModal();
      } else {
        message.error(response.error || "Failed to delete LinkedIn account");
      }
    }
  } catch (error) {
    console.error("Error deleting LinkedIn account:", error);
    message.error("An error occurred while deleting the LinkedIn account");
    hideDeleteModal();
  }
};

const customRow = (row) => {
  return {
    onClick: () => editAccount(row),
    style: "cursor: pointer",
  };
};

const fetchLinkedInAccounts = async () => {
  try {
    const response = await DashboardService.fetchAllLinkedInIntegrations();
    if (response?.success) {
      if (response.notification === 'already_connected') {
        message.warning('You already have an active connection with this LinkedIn account.');
      }
      linkedinAccounts.value = response.linkedin_accounts.map(account => {
        const displayName = account.name ;

        return {
          ...account,
          account_name: {
            text: displayName,
            is_default: account.is_default || false
          },
          status: account.status === 'CONNECTED' ? 'Connected' : 'Disconnected'
        };
      });
      
    }
  } catch (error) {
    console.error("Error fetching LinkedIn accounts:", error);
  }
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      checkConnection(),
      fetchBillingStatus(),
      fetchLinkedInAccounts()
    ]);
  } catch (error) {
    console.error("Error loading LinkedIn accounts:", error);
  } finally {
    isLoading.value = false;
  }

  if (mqttService.isClientConnected()) {
    await subscribeToLinkedInSync();
  } else {
    mqttService.client?.on('connect', subscribeToLinkedInSync);
  }
});

const onWindowFocus = () => fetchLinkedInAccounts();
window.addEventListener('focus', onWindowFocus);

let linkedinMqttTopic = null;

const subscribeToLinkedInSync = async () => {
  try {
    const workspaceId = WorkspaceService.getCurrentWorkspaceId();
    const userId = Number(localStorage.getItem("userId"));
    if (!workspaceId || !userId) return;

    const environment = getCurrentEnvironment();
    linkedinMqttTopic = `${environment}/linkedin/sync/${workspaceId}/${userId}`;

    await mqttService.subscribe(linkedinMqttTopic, () => {
      fetchLinkedInAccounts();
    });
  } catch (error) {
    console.error("Failed to subscribe to LinkedIn sync topic:", error);
  }
};

const unsubscribeFromLinkedInSync = async () => {
  try {
    if (linkedinMqttTopic) {
      await mqttService.unsubscribe(linkedinMqttTopic);
      linkedinMqttTopic = null;
    }
  } catch (error) {
    console.error("Failed to unsubscribe from LinkedIn sync topic:", error);
  }
};

onUnmounted(async () => {
  window.removeEventListener('focus', onWindowFocus);
  mqttService.client?.off('connect', subscribeToLinkedInSync);
  await unsubscribeFromLinkedInSync();
});
</script>